<script setup>
// Libraries
import { storeToRefs } from 'pinia';

// Composables
import { getEnv } from '@/composables/helpers/getEnv';
import { debounce } from '@/composables/helpers/performance';

// Stores
import { useGlobalStore } from '~/stores/global';

// PostHog
const { $clientPosthog } = useNuxtApp();

const props = defineProps({
    initialQuery: {
        type: String,
        required: false,
        default: '',
    },
    searchOptions: {
        type: Object,
        required: false,
        default: () => ({
            requestOptions: {
            },
        }),
    },
});

const emit = defineEmits(['closeSearch']);

// Variables
const environment = getEnv('builderEnv');
const { searchOptions } = storeToRefs(useGlobalStore());
const { $bus } = useNuxtApp();
const query = ref();
const data = ref();
const searchInput = ref();
const loading = ref(false);

const index = {
    Local: 'staging_index',
    Staging: 'staging_index',
    Production: 'production_index',
    TwentyFive: 'production_index',
    Unbound: 'production_index',
};

// Libraries
const { search } = useAlgoliaSearch(index[environment]);

const hits = computed(() => {
    if (!data.value) return false;
    const { hits } = data.value;
    if (!hits) return false;
    return hits;
});

const noResults = computed(() => {
    if (!data.value) return false;
    const { hits } = data.value;
    if (!hits) return false;
    return hits.length === 0;
});

// Methods
const updateResult = async () => {
    if (!query.value) {
        query.value = '';
        data.value = [];
        loading.value = false;
        return;
    }
    data.value = await search({ query: query.value, ...props.searchOptions });
    loading.value = false;

    // Track search with results count
    $clientPosthog?.capture('search_performed', {
        query: query.value,
        results_count: data.value?.hits?.length || 0,
        has_results: (data.value?.hits?.length || 0) > 0
    });
};

const onSubmit = () => {
    loading.value = true
    updateResult();
};

const clearSearch = () => {
    query.value = '';
    data.value = [];
};

// Life cycle
watch(query, debounce(onSubmit, 500));

const setFocus = () => {
    if (searchInput.value) searchInput.value.focus();
};

onMounted(() => {
    if (props.initialQuery) query.value = props.initialQuery;
    if (query.value) updateResult(query);
    if (process.client) setTimeout(setFocus(), 250);
});

</script>
<template>
    <div class="search-box biox">
        <form @submit.prevent="onSubmit">
            <div class="input-wrapper" :class="query && query.length ? 'has-value' : ''">
                <input v-model="query" type="text" placeholder="Search UNBOUND" id="search-field" ref="searchInput" />
            </div>
            <ProjectButton :button="{ label: 'Search', title: `Search UNBOUND for ${query}` }" type="submit" @click.prevent="onSubmit" class="search-text-btn" />
            <button type="submit" @click.prevent="onSubmit" class="search-icon-btn" aria-label="Search">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            </button>
        </form>
        <div v-if="loading || noResults || hits" class="results">
            <template v-if="loading">
                <h2 class="h2">Searching</h2>
                <div class="content loader"></div>
            </template>
            <template v-else-if="noResults">
                <h2 class="h2">No Results Found</h2>
                <div class="content">
                    <p>There are 0 results for <strong>{{ query }}</strong>. Try searching for something else.</p>
                </div>
            </template>
            <template v-else-if="hits">
                <h2 class="h2">Your Search Results</h2>
                <div class="content">
                    <ul>
                        <li v-for="hit in hits" :key="hit.id">
                            <FragmentsAlgoliaHit @click="emit('closeSearch')" :hit="hit" :key="hit.id" />
                        </li>
                    </ul>
                </div>
            </template>
        </div>
    </div>
</template>

<style lang="scss">
.search-modal {
    
    &.search {
        height: auto;
    }
    
    .search-box {

        #search-field {
            padding: 1rem 1.5rem;
            color: white;
            font-size: 16px;
            font-style: normal;
            font-weight: 350;
            line-height: 156.1%;
            letter-spacing: 0.24px;
        }

        .results {
            margin-top: 2.5rem;
        }

        form {
            display: flex;

            .input-wrapper {
                flex: 1;
                position: relative;

                input {
                    width: 100%;
                    border: 1.5px solid var(--sage);
                    border-radius: 10px;
                    color: white;
                    font-family: var(--font-hubspot-sans);

                    &::placeholder {
                        color: var(--sage);
                    }

                    @media (min-width: $tablet) {
                        border-radius: 10px 0px 0px 10px;
                    }
                }

                input:-webkit-autofill {
                    -webkit-box-shadow: 0 0 0 50px white inset;
                    -webkit-text-fill-color: white;
                }

                input:-webkit-autofill:focus {
                    -webkit-box-shadow: 0 0 0 50px white inset;
                    -webkit-text-fill-color: white;
                }

                button {
                    position: absolute;
                    border: 0;
                    padding: 0;
                    right: 1rem;
                    top: 50%;
                    transform: translate(0, -50%);
                    color: var(--black);
                    display: none;

                    &:hover {
                        background: var(--orange);
                        color: white;
                    }
                }

                &.has-value {
                    button {
                        display: block;
                    }
                }
            }

            button {
                color: white;
                text-align: center;
                font-family: var(--font-hubspot-sans);
                font-size: 0.8125rem;
                font-style: normal;
                font-weight: 400;
                line-height: normal;
                letter-spacing: 0.065rem;
                text-transform: uppercase;
                // padding: 1.2rem;
                border-right: 1.5px solid var(--sage);
                border-top: 1.5px solid var(--sage);
                border-bottom: 1.5px solid var(--sage);
                transition: all 0.33s ease;
                border-left: none;
                border-radius: 0 var(--radius) var(--radius) 0;
                padding: 1.125rem 1.25rem;

                &:hover {
                    background: var(--orange);
                    color: white;
                    border-color: var(--orange);
                }
            }
        }

        h2 {
            color: var(--cream);
            font-family: var(--font-hubspot-sans);
            font-size: 1.875rem;
            line-height: 2rem;
            margin-bottom: 2.125rem;
        }

        .content {
            color: var(--sage);
            font-family: var(--font-hubspot-sans);
            max-height: none;

            &.loader {
                display: flex;
                justify-content: center;
                align-items: center;
            }
        }

        ul {
            @include styled-scrollbar;

            margin-top: 1rem;
            max-height: none;
            overflow-y: visible;
            overflow-x: hidden;

            @media (min-width: $tablet) {
                max-height: calc(50dvh);
                overflow-y: auto;
            }

            li {
                position: relative;
                user-select: none;

                &:last-child {
                    @media (max-width: $tablet) {
                        margin-bottom: 4rem;
                    }
                }

                +li {
                    margin-top: 1.5rem;
                    padding-top: 1.5rem;
                    border-top: 0.5px solid var(--teal-white);

                    &::before {
                        content: '';
                        display: block;
                        width: 100%;
                        height: 1px;
                        background: var(--gray-500);
                        position: absolute;
                        top: 0;
                    }
                }
            }
        }

        .search-icon-btn {
            display: none;
        }

        @media (max-width: $tablet) {
            height: auto;

            ul {
                // max-height: calc(100% - 10rem);
            }

            form {
                .search-text-btn {
                    display: none;
                }

                button.search-icon-btn {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: var(--magenta) !important;
                    color: white !important;
                    border: 1.5px solid var(--magenta) !important;
                    border-left: none !important;
                    border-radius: 0 var(--radius) var(--radius) 0;
                    padding: 0.75rem 1rem;
                    margin: 0;
                    cursor: pointer;
                    transition: background 0.2s ease, color 0.2s ease;

                    &:hover {
                        background: var(--orange) !important;
                        color: white !important;
                        border-color: var(--orange) !important;
                    }
                }
            }
        }
    }
}
</style>
